import Links from '@/shared/constants/navegacao';
import {
  BreadcrumbActionsContext,
  type BreadcrumbActionsConfig,
} from '@/shared/contexts/BreadcrumbActionsContext';
import {
  getBreadcrumbLabel,
  VINCULOS_REDIRECT_SEGMENTS,
} from '@/shared/navigation/breadcrumb-trail.config';
import { isIdSegment } from '@/shared/utils/rotaVoltar';
import { useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

export const ACTION_SLOT_ORDER = [
  'primary',
  'secondary',
  'tertiary',
  'print',
  'history',
] as const;

export type ActionSlotKey = (typeof ACTION_SLOT_ORDER)[number];

function tituloPagina(pathname: string): string {
  if (pathname === Links.Index || pathname === Links.Home) return 'Início';
  const segmento = pathname.split('/').filter(Boolean)[0] ?? 'SGI';
  return segmento.charAt(0).toUpperCase() + segmento.slice(1).replace(/-/g, ' ');
}

export function useAppPageHeader() {
  const location = useLocation();
  const actionsContext = useContext(BreadcrumbActionsContext);

  const actions: BreadcrumbActionsConfig = actionsContext?.actions ?? {};
  const headerOptions = actions.header ?? {};

  const pathnames = location.pathname.split('/').filter((x) => x);
  const isPaginaInicial =
    pathnames.length <= 0 || location.pathname === '/' || location.pathname === '/home';

  const showBreadcrumb = headerOptions.showBreadcrumb ?? !isPaginaInicial;

  const actionItems = useMemo(
    () =>
      ACTION_SLOT_ORDER.map((slot) => actions[slot]).filter(
        (action): action is NonNullable<typeof action> => Boolean(action),
      ),
    [actions],
  );

  const hasActions = actionItems.length > 0;
  const showActionBar = hasActions;

  const titulo = headerOptions.title ?? tituloPagina(location.pathname);

  const breadcrumbItems = useMemo(() => {
    if (!showBreadcrumb) return [];

    return pathnames
      .map((value, index) => {
        const nextSegment = pathnames[index + 1];
        const isLast = index === pathnames.length - 1;

        let to = `/${pathnames.slice(0, index + 1).join('/')}`;
        if (isIdSegment(nextSegment)) {
          to = `/${pathnames.slice(0, index + 2).join('/')}`;
        }

        if (VINCULOS_REDIRECT_SEGMENTS.includes(value) && !isLast) {
          to = '/vinculos';
        }

        if (isIdSegment(value)) return null;

        return {
          key: to,
          label: getBreadcrumbLabel(value),
          to,
          isLast,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item != null);
  }, [pathnames, showBreadcrumb]);

  return {
    titulo,
    showBreadcrumb,
    showActionBar,
    hasActions,
    actionItems,
    actions,
    breadcrumbItems,
    isPaginaInicial,
  };
}
